import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calender.css';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import { Button, Input, Modal } from '../../Components/UI';
import {
  FaCalendarAlt,
  FaPlus,
  FaTrashAlt,
  FaSync,
  FaBriefcase,
  FaUmbrellaBeach,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import { formatExportDate } from '../../UtilityHookFunctions/fetchAllPages';
import { useGetLoggedInUser } from '../../UtilityHookFunctions/LoggedInUser';

const localizer = momentLocalizer(moment);
const API_BASE_URL = `${END_POINT}/calendar`;

const LeaveCalendar = ({ embedded = false }) => {
  const loggedInUser = useGetLoggedInUser();
  const isMainDoctor = !loggedInUser?.role || loggedInUser?.role === 'MainDoctor' || loggedInUser?.role === 'admin';

  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [typeFilter, setTypeFilter] = useState('ALL');

  const [formData, setFormData] = useState({
    title: '',
    eventType: 'holiday',
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    allDay: true,
    description: '',
    recurrence: 'single',
    selectedMonth: moment().month(),
    selectedYear: moment().year(),
    dayOfWeek: 0,
  });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API_BASE_URL, {
        headers: {
          Authorization: getJwtToken(),
        },
      });

      const parsed = (res.data || []).map((e) => {
        const titleUpper = (e.title || '').toUpperCase();
        let derivedType = e.eventType;
        if (!derivedType) {
          if (titleUpper.includes('HOLIDAY')) derivedType = 'holiday';
          else if (titleUpper.includes('WORKING')) derivedType = 'working_day';
          else if (titleUpper.includes('SUNDAY')) derivedType = 'sunday';
          else derivedType = 'event';
        }

        return {
          ...e,
          id: e._id,
          eventType: derivedType,
          start: new Date(e.start),
          end: new Date(e.end),
        };
      });

      setEvents(parsed);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load holiday calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    if (typeFilter === 'ALL') return events;
    return events.filter((e) => {
      if (typeFilter === 'holiday') return e.eventType === 'holiday';
      if (typeFilter === 'working_day') return e.eventType === 'working_day';
      if (typeFilter === 'sunday') {
        return e.eventType === 'sunday' || (e.title || '').toUpperCase().includes('SUNDAY');
      }
      return true;
    });
  }, [events, typeFilter]);

  const stats = useMemo(() => {
    let holidays = 0;
    let workingDays = 0;
    let sundays = 0;
    events.forEach((e) => {
      const t = (e.title || '').toUpperCase();
      if (e.eventType === 'holiday' || t.includes('HOLIDAY')) holidays += 1;
      else if (e.eventType === 'working_day' || t.includes('WORKING')) workingDays += 1;
      else if (e.eventType === 'sunday' || t.includes('SUNDAY')) sundays += 1;
    });
    return { holidays, workingDays, sundays, total: events.length };
  }, [events]);

  const handleSelectSlot = ({ start, end }) => {
    const startStr = moment(start).format('YYYY-MM-DD');
    const endMoment = moment(end).isSame(moment(start), 'day')
      ? moment(start)
      : moment(end).subtract(1, 'day').isSameOrAfter(moment(start), 'day')
      ? moment(end).subtract(1, 'day')
      : moment(start);

    setSelectedEvent(null);
    setFormData({
      title: '',
      eventType: 'holiday',
      startDate: startStr,
      endDate: endMoment.format('YYYY-MM-DD'),
      allDay: true,
      description: '',
      recurrence: 'single',
      selectedMonth: moment(start).month(),
      selectedYear: moment(start).year(),
      dayOfWeek: moment(start).day(),
    });
    setError('');
    setSuccessMsg('');
    setShowModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title || '',
      eventType: event.eventType || 'holiday',
      startDate: moment(event.start).format('YYYY-MM-DD'),
      endDate: moment(event.end).format('YYYY-MM-DD'),
      allDay: Boolean(event.allDay),
      description: event.description || '',
      recurrence: 'single',
      selectedMonth: moment(event.start).month(),
      selectedYear: moment(event.start).year(),
      dayOfWeek: moment(event.start).day(),
    });
    setError('');
    setSuccessMsg('');
    setShowModal(true);
  };

  const openNewEventModal = (defaultType = 'holiday') => {
    setSelectedEvent(null);
    const today = moment().format('YYYY-MM-DD');
    setFormData({
      title: defaultType === 'holiday' ? 'Company Holiday' : 'Special Working Day',
      eventType: defaultType,
      startDate: today,
      endDate: today,
      allDay: true,
      description: '',
      recurrence: 'single',
      selectedMonth: moment().month(),
      selectedYear: moment().year(),
      dayOfWeek: 0,
    });
    setError('');
    setSuccessMsg('');
    setShowModal(true);
  };

  const generateRecurringDates = () => {
    const { recurrence, selectedMonth, selectedYear, dayOfWeek, startDate, endDate } = formData;

    if (recurrence === 'whole_month') {
      const dates = [];
      const m = moment([selectedYear, selectedMonth, 1]);
      const daysInMonth = m.daysInMonth();
      for (let day = 1; day <= daysInMonth; day++) {
        const d = moment([selectedYear, selectedMonth, day]);
        if (d.day() === Number(dayOfWeek)) {
          dates.push(d.format('YYYY-MM-DD'));
        }
      }
      return dates;
    }

    if (recurrence === 'whole_year') {
      const dates = [];
      for (let month = 0; month < 12; month++) {
        const m = moment([selectedYear, month, 1]);
        const daysInMonth = m.daysInMonth();
        for (let day = 1; day <= daysInMonth; day++) {
          const d = moment([selectedYear, month, day]);
          if (d.day() === Number(dayOfWeek)) {
            dates.push(d.format('YYYY-MM-DD'));
          }
        }
      }
      return dates;
    }

    const startM = moment(startDate);
    const endM = moment(endDate);
    if (!startM.isValid() || !endM.isValid()) return [];

    const dates = [];
    let cur = startM.clone();
    while (cur.isSameOrBefore(endM, 'day')) {
      dates.push(cur.format('YYYY-MM-DD'));
      cur.add(1, 'day');
    }
    return dates;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.title.trim()) {
      setError('Title / Reason is required');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMsg('');

    try {
      const isUpdating = Boolean(selectedEvent?._id);

      if (isUpdating) {
        const startDateTime = new Date(`${formData.startDate}T00:00:00`);
        const endDateTime = new Date(`${formData.endDate}T23:59:59`);

        const payload = {
          title: formData.title.trim(),
          start: startDateTime,
          end: endDateTime,
          allDay: true,
          eventType: formData.eventType,
          description: formData.description,
        };

        await axios.put(`${API_BASE_URL}/${selectedEvent._id}`, payload, {
          headers: {
            Authorization: getJwtToken(),
          },
        });

        setSuccessMsg('Calendar item updated successfully!');
      } else {
        if (formData.recurrence === 'single') {
          const dates = generateRecurringDates();
          if (dates.length <= 1) {
            const startDateTime = new Date(`${formData.startDate}T00:00:00`);
            const endDateTime = new Date(`${formData.endDate}T23:59:59`);

            await axios.post(
              API_BASE_URL,
              {
                title: formData.title.trim(),
                start: startDateTime,
                end: endDateTime,
                allDay: true,
                eventType: formData.eventType,
                description: formData.description,
              },
              {
                headers: {
                  Authorization: getJwtToken(),
                },
              }
            );
          } else {
            const bulkEvents = dates.map((dateStr) => ({
              title: formData.title.trim(),
              start: new Date(`${dateStr}T00:00:00`),
              end: new Date(`${dateStr}T23:59:59`),
              allDay: true,
              eventType: formData.eventType,
              description: formData.description,
            }));

            await axios.post(
              API_BASE_URL,
              { bulkEvents },
              {
                headers: {
                  Authorization: getJwtToken(),
                },
              }
            );
          }
        } else {
          const dates = generateRecurringDates();
          if (dates.length === 0) {
            setError('No matching days found for the selected month/year.');
            setSubmitting(false);
            return;
          }

          const bulkEvents = dates.map((dateStr) => ({
            title: formData.title.trim(),
            start: new Date(`${dateStr}T00:00:00`),
            end: new Date(`${dateStr}T23:59:59`),
            allDay: true,
            eventType: formData.eventType,
            description: formData.description,
          }));

          await axios.post(
            API_BASE_URL,
            { bulkEvents },
            {
              headers: {
                Authorization: getJwtToken(),
              },
            }
          );
        }

        setSuccessMsg('Holiday/Working Day added successfully!');
      }

      setShowModal(false);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save calendar entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent?._id) return;
    if (!window.confirm(`Are you sure you want to delete "${selectedEvent.title}"?`)) return;

    setSubmitting(true);
    try {
      await axios.delete(`${API_BASE_URL}/${selectedEvent._id}`, {
        headers: {
          Authorization: getJwtToken(),
        },
      });
      setShowModal(false);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete event');
    } finally {
      setSubmitting(false);
    }
  };

  const getEventStyle = (event) => {
    const title = (event.title || '').toUpperCase();
    const type = event.eventType;

    if (type === 'holiday' || title.includes('HOLIDAY')) {
      return {
        className: 'transition-all hover:scale-[1.01]',
        style: {
          background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
          borderRadius: '8px',
          border: 'none',
          color: '#ffffff',
          fontWeight: '600',
          boxShadow: '0 1px 3px rgba(225, 29, 72, 0.25)',
        },
      };
    }

    if (type === 'working_day' || title.includes('WORKING DAY')) {
      return {
        className: 'transition-all hover:scale-[1.01]',
        style: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '8px',
          border: 'none',
          color: '#ffffff',
          fontWeight: '600',
          boxShadow: '0 1px 3px rgba(5, 150, 105, 0.25)',
        },
      };
    }

    if (type === 'sunday' || title.includes('SUNDAY')) {
      return {
        className: 'transition-all hover:scale-[1.01]',
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '8px',
          border: 'none',
          color: '#ffffff',
          fontWeight: '600',
          boxShadow: '0 1px 3px rgba(217, 119, 6, 0.25)',
        },
      };
    }

    return {
      className: 'transition-all hover:scale-[1.01]',
      style: {
        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
        borderRadius: '8px',
        border: 'none',
        color: '#ffffff',
        fontWeight: '600',
        boxShadow: '0 1px 3px rgba(2, 132, 199, 0.25)',
      },
    };
  };

  const previewCount = useMemo(() => {
    return generateRecurringDates().length;
  }, [formData]);

  const content = (
    <div className={embedded ? '' : 'p-4 md:p-6 bg-slate-50 min-h-screen'}>
      {/* Top Header Card */}
      <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-4 md:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center text-lg">
                <FaUmbrellaBeach />
              </div>
              Leave & Holiday Calendar
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Mark official company holidays and special working days. Option to repeat across an entire month or whole year.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2.5 self-start lg:self-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEvents}
              disabled={loading}
              icon={<FaSync className={loading ? 'animate-spin' : ''} />}
            >
              Refresh
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={loading || !filteredEvents.length}
              onClick={() =>
                exportToExcel(filteredEvents, 'Holiday_Calendar', [
                  { key: 'title', label: 'Title' },
                  { key: 'eventType', label: 'Type', format: (v) => String(v || '').replace(/_/g, ' ') },
                  { key: 'start', label: 'Start Date', format: formatExportDate },
                  { key: 'end', label: 'End Date', format: formatExportDate },
                  { key: 'description', label: 'Description' },
                ])
              }
            >
              Export to Excel
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => openNewEventModal('working_day')}
              className="!border-emerald-500 !text-emerald-700 hover:!bg-emerald-50"
              icon={<FaBriefcase />}
            >
              + Add Working Day
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => openNewEventModal('holiday')}
              className="!bg-rose-600 hover:!bg-rose-700 !border-rose-600 shadow-sm"
              icon={<FaPlus />}
            >
              + Add Company Holiday
            </Button>
          </div>
        </div>

        {/* Stats and Filter Badges */}
        <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          {/* Quick Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 mr-1">Filter View:</span>
            {[
              { key: 'ALL', label: `All (${stats.total})` },
              { key: 'holiday', label: `Holidays (${stats.holidays})`, color: 'text-rose-700 bg-rose-50 border-rose-200' },
              { key: 'working_day', label: `Working Days (${stats.workingDays})`, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
              { key: 'sunday', label: `Sundays (${stats.sundays})`, color: 'text-amber-700 bg-amber-50 border-amber-200' },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setTypeFilter(f.key)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition border ${
                  typeFilter === f.key
                    ? 'bg-slate-800 text-white border-slate-800 shadow-xs'
                    : f.color || 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Color Legend */}
          <div className="flex items-center gap-3 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-rose-700">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
              Holiday
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1.5 text-emerald-700">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
              Working Day
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1.5 text-amber-700">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              Sunday
            </span>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaExclamationTriangle />
              <span>{error}</span>
            </div>
            <button
              type="button"
              onClick={() => setError('')}
              className="text-rose-500 hover:text-rose-700 font-bold ml-2"
            >
              &times;
            </button>
          </div>
        )}

        {successMsg && (
          <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaCheckCircle />
              <span>{successMsg}</span>
            </div>
            <button
              type="button"
              onClick={() => setSuccessMsg('')}
              className="text-emerald-500 hover:text-emerald-700 font-bold ml-2"
            >
              &times;
            </button>
          </div>
        )}
      </div>

      {/* Big Calendar Main Card */}
      <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-4 md:p-6">
        <div className="w-full h-[650px] md:h-[750px]">
          <BigCalendar
            localizer={localizer}
            events={filteredEvents}
            selectable
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%', width: '100%' }}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={getEventStyle}
          />
        </div>
      </div>

      {/* Modal: Add or Edit Holiday / Working Day */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          selectedEvent
            ? 'Edit Calendar Day'
            : 'Mark Holiday or Working Day'
        }
        subtitle={
          selectedEvent
            ? 'View or modify details for this calendar entry.'
            : 'Configure official company holiday or special working day schedule.'
        }
        maxWidth="max-w-lg"
        footer={
          <div className="flex items-center justify-between w-full">
            <div>
              {selectedEvent && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  disabled={submitting}
                  icon={<FaTrashAlt />}
                >
                  Delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
                className={formData.eventType === 'holiday' ? '!bg-rose-600 hover:!bg-rose-700' : '!bg-emerald-600 hover:!bg-emerald-700'}
              >
                {submitting
                  ? 'Saving...'
                  : selectedEvent
                  ? 'Update Entry'
                  : `Save (${previewCount} Day${previewCount > 1 ? 's' : ''})`}
              </Button>
            </div>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4 text-slate-800">
          {/* Day Type Selector */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">
              Entry Type <span className="text-rose-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, eventType: 'holiday' })}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-sm font-semibold transition ${
                  formData.eventType === 'holiday'
                    ? 'bg-rose-50 border-rose-500 text-rose-700 ring-2 ring-rose-500/20'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <FaUmbrellaBeach className="text-rose-500" />
                Company Holiday
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, eventType: 'working_day' })}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-sm font-semibold transition ${
                  formData.eventType === 'working_day'
                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700 ring-2 ring-emerald-500/20'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <FaBriefcase className="text-emerald-500" />
                Working Day
              </button>
            </div>
          </div>

          {/* Title / Name */}
          <Input
            label="Name / Reason"
            name="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={
              formData.eventType === 'holiday'
                ? 'e.g. Diwali Holiday, Republic Day, Annual Clinic Off'
                : 'e.g. Special Camp Sunday, Emergency Duty Working Day'
            }
            required
          />

          {/* Recurrence Mode (Only when creating new entry) */}
          {!selectedEvent && (
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">
                Application Scope / Repeat Option
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, recurrence: 'single' })}
                  className={`py-2 px-3 rounded-xl border text-xs font-semibold transition text-center ${
                    formData.recurrence === 'single'
                      ? 'bg-sky-50 border-sky-500 text-sky-700 ring-1 ring-sky-500/20'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Specific Date(s)
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, recurrence: 'whole_month' })}
                  className={`py-2 px-3 rounded-xl border text-xs font-semibold transition text-center ${
                    formData.recurrence === 'whole_month'
                      ? 'bg-sky-50 border-sky-500 text-sky-700 ring-1 ring-sky-500/20'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  For Month
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, recurrence: 'whole_year' })}
                  className={`py-2 px-3 rounded-xl border text-xs font-semibold transition text-center ${
                    formData.recurrence === 'whole_year'
                      ? 'bg-sky-50 border-sky-500 text-sky-700 ring-1 ring-sky-500/20'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  For Whole Year
                </button>
              </div>
            </div>
          )}

          {/* Date controls for single date / date range */}
          {formData.recurrence === 'single' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block mb-1 text-xs font-semibold text-slate-700">
                  Start Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={(e) => {
                    const nextStart = e.target.value;
                    const nextEnd = moment(formData.endDate).isBefore(nextStart) ? nextStart : formData.endDate;
                    setFormData({ ...formData, startDate: nextStart, endDate: nextEnd });
                  }}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-500"
                />
              </div>

              <div>
                <label className="block mb-1 text-xs font-semibold text-slate-700">
                  End Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  min={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-500"
                />
              </div>
            </div>
          )}

          {/* Month & Weekday selection when recurring for a Month */}
          {formData.recurrence === 'whole_month' && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 text-xs font-semibold text-slate-700">Month</label>
                  <select
                    value={formData.selectedMonth}
                    onChange={(e) => setFormData({ ...formData, selectedMonth: Number(e.target.value) })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  >
                    {moment.months().map((m, idx) => (
                      <option key={idx} value={idx}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-semibold text-slate-700">Year</label>
                  <select
                    value={formData.selectedYear}
                    onChange={(e) => setFormData({ ...formData, selectedYear: Number(e.target.value) })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  >
                    {[moment().year() - 1, moment().year(), moment().year() + 1, moment().year() + 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block mb-1 text-xs font-semibold text-slate-700">
                  Select Recurring Day of Week
                </label>
                <select
                  value={formData.dayOfWeek}
                  onChange={(e) => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 font-semibold"
                >
                  <option value={0}>Every Sunday of this month</option>
                  <option value={1}>Every Monday of this month</option>
                  <option value={2}>Every Tuesday of this month</option>
                  <option value={3}>Every Wednesday of this month</option>
                  <option value={4}>Every Thursday of this month</option>
                  <option value={5}>Every Friday of this month</option>
                  <option value={6}>Every Saturday of this month</option>
                </select>
              </div>

              <div className="text-xs text-sky-700 bg-sky-50 p-2.5 rounded-lg border border-sky-100 flex items-center justify-between">
                <span>Total matching days to add:</span>
                <span className="font-bold text-sm bg-white px-2 py-0.5 rounded border border-sky-200">
                  {previewCount}
                </span>
              </div>
            </div>
          )}

          {/* Year & Weekday selection when recurring for whole Year */}
          {formData.recurrence === 'whole_year' && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 text-xs font-semibold text-slate-700">Calendar Year</label>
                  <select
                    value={formData.selectedYear}
                    onChange={(e) => setFormData({ ...formData, selectedYear: Number(e.target.value) })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  >
                    {[moment().year() - 1, moment().year(), moment().year() + 1, moment().year() + 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-xs font-semibold text-slate-700">
                    Recurring Day
                  </label>
                  <select
                    value={formData.dayOfWeek}
                    onChange={(e) => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 font-semibold"
                  >
                    <option value={0}>Every Sunday (All 12 Months)</option>
                    <option value={1}>Every Monday (All 12 Months)</option>
                    <option value={2}>Every Tuesday (All 12 Months)</option>
                    <option value={3}>Every Wednesday (All 12 Months)</option>
                    <option value={4}>Every Thursday (All 12 Months)</option>
                    <option value={5}>Every Friday (All 12 Months)</option>
                    <option value={6}>Every Saturday (All 12 Months)</option>
                  </select>
                </div>
              </div>

              <div className="text-xs text-sky-700 bg-sky-50 p-2.5 rounded-lg border border-sky-100 flex items-center justify-between">
                <span>Total matching days to add across all 12 months:</span>
                <span className="font-bold text-sm bg-white px-2 py-0.5 rounded border border-sky-200">
                  {previewCount}
                </span>
              </div>
            </div>
          )}

          {/* Description / Notes */}
          <div>
            <label className="block mb-1 text-xs font-semibold text-slate-700">
              Description / Notes
            </label>
            <textarea
              rows={2}
              name="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g. Clinic will remain closed for all doctors and staff members."
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-500 resize-none"
            />
          </div>
        </form>
      </Modal>
    </div>
  );

  return embedded ? content : <RightWrapper>{content}</RightWrapper>;
};

export default LeaveCalendar;
